import React from 'react';
import LogRocket from 'logrocket';
import { useDispatch, useSelector } from 'react-redux';
import { useFeatureFlagRedux } from '../../hooks';
import nonCriticalException from '../exceptionLogger';
import { fetchPaymentSettings } from '../provider/PaymentSettings';
import type { IRootDispatch, RootState } from '../../store/models';
import type store from '../../store';
import { TAP_TO_PAY_FEATURE_FLAG } from '../../components/provider/checkout/TapToPayFlow/constants';
import { onAppResume } from '../onAppResume';
import { log } from './TapToPayLogger';
import { waitForReaderConnection } from './waitForReaderConnection';

type UseTapToPayReaderConnection = {
  connectReader: () => Promise<void>,
  initReaderConnection: () => Promise<void>,
  killReaderConnection: () => Promise<void>,
};

export type UseTapToPayReaderConnectionParams = {
  /** The Stripe Terminal Location's ID */
  locationId: string;
  /** The name shown on the reader's screen */
  merchantDisplayName: string;
  /** The provider's ID, used to fetch the provider's stripe account ID before connecting */
  providerId: number;
  /** Needed for waitForReaderConnection */
  getState: () => ReturnType<typeof store.getState>;
  /** Callback for when the connection attempt is about to start.
   * **Note: Remember to use React.useCallback** */
  onConnectStart?: () => void;
  /** Callback for when the connection attempt succeeded
   * **Note: Remember to use React.useCallback** */
  onConnectSuccess?: () => void;
  /** Callback for when the connection attempt failed
   * **Note: Remember to use React.useCallback** */
  onConnectFailure?: (error?: Error) => void;
  /** Callback for when the connection attempt is finished (applies for both success and failure)
   * **Note: Remember to use React.useCallback** */
  onConnectFinished?: () => void;
};

export type UseTapToPayReaderConnectionOptions = {
  /** Use a simulated connection for testing */
  isSimulated: boolean;
  /** Install the reconnection effect, defaults to false */
  installReconnectHandler?: boolean;
  /** When simulating a connection, how long to wait until success. In seconds */
  simulationTime?: number;
};

const DEFAULT_SIMULATION_TIME = 2;

/** Handles connections & reconnections when the app was sent to the background and was brought back
 * to the foreground
 *
 * The hook is inert until initReaderConnection() is called, at which point it'll attempt to connect
 * to the reader. Connections can take between a couple seconds. However, if this is on a new device
 * that's never had tap to pay enabled before, the connection attempt may take a minute or so.
 *
 * If installReconnectHandler is true, then we'll also add the handler to reconnect to the reader
 * after the app is resumed, since Apple disconnects the reader when the app is backgrounded.
 *
 * The handler will only reconnect to the reader when shouldAutoReconnect is true. The intention
 * is that when shouldAutoReconnect is false, we don't want to reconnect even with an active
 * handler, such as when no pro is logged in.
 */
export function useTapToPayReaderConnection(
  {
    locationId,
    merchantDisplayName,
    providerId,
    getState,
    onConnectStart,
    onConnectSuccess,
    onConnectFailure,
    onConnectFinished,
  }: UseTapToPayReaderConnectionParams,
  {
    isSimulated,
    installReconnectHandler = false,
    simulationTime = DEFAULT_SIMULATION_TIME,
  }: UseTapToPayReaderConnectionOptions,
): UseTapToPayReaderConnection {
  const [isInitialized, setIsInitialized] = React.useState(false);
  const tapToPayFeatureFlag = useFeatureFlagRedux(TAP_TO_PAY_FEATURE_FLAG);
  const tapToPayEnabled = React.useMemo(
    () => !tapToPayFeatureFlag.loading && tapToPayFeatureFlag.enabled,
    [tapToPayFeatureFlag],
  );
  const dispatch = useDispatch<IRootDispatch>();
  const shouldAutoReconnect = useSelector<RootState, boolean>(
    ({ stripeTerminal }) => stripeTerminal.shouldAutoReconnect,
  );

  const connect = React.useCallback(async () => {
    onConnectStart?.();
    dispatch.stripeTerminal.onReaderConnecting();
    try {
      // Retrieve Stripe account data for the Pro
      const { stripe_account_id: stripeAcctId } = await fetchPaymentSettings(providerId);
      // Attempt to connect to card reader
      await dispatch.stripeTerminal.connectReader({
        locationId,
        merchantName: merchantDisplayName,
        stripeAcctId,
      });
      onConnectSuccess?.();
    } catch (e) {
      log(`Failed to connect: ${e}`);
      nonCriticalException(e);
      const err = e instanceof Error ? e : new Error(String(e));
      LogRocket.captureException(err, {
        tags: {
          TapToPay: 'Connect',
        },
      });

      onConnectFailure?.(err);
    } finally {
      if (installReconnectHandler) {
        dispatch.stripeTerminal.setShouldAutoReconnect(true);
      }
      onConnectFinished?.();
    }
  }, [
    dispatch.stripeTerminal,
    installReconnectHandler,
    locationId,
    merchantDisplayName,
    onConnectStart,
    onConnectFailure,
    onConnectFinished,
    onConnectSuccess,
    providerId,
  ]);

  const simulateConnect = React.useCallback(async () => {
    onConnectStart?.();
    dispatch.stripeTerminal.onReaderConnecting();
    log('Simulated connection to reader: Connecting');
    return new Promise<void>(resolve => {
      setTimeout(() => {
        dispatch.stripeTerminal.onReaderConnected();
        dispatch.stripeTerminal.setShouldAutoReconnect(true);
        onConnectSuccess?.();
        onConnectFinished?.();
        log('Simulated connection to reader: Connected');
        resolve();
      }, simulationTime * 1000);
    });
  }, [
    dispatch.stripeTerminal,
    onConnectStart,
    onConnectSuccess,
    onConnectFinished,
    simulationTime,
  ]);

  const connectReader = React.useCallback(
    async () => {
      if (!tapToPayEnabled) return;

      if (isSimulated) {
        await simulateConnect();
      } else {
        await connect();
      }
    },
    [
      connect,
      isSimulated,
      simulateConnect,
      tapToPayEnabled,
    ],
  );

  const initReaderConnection = React.useCallback(
    async () => {
      if (isInitialized) return;

      // Set this asap to prevent repeat inits
      setIsInitialized(true);

      await connectReader();
    }, [
      connectReader,
      isInitialized,
    ],
  );

  const killReaderConnection = React.useCallback(
    async () => {
      if (!isInitialized) return;

      setIsInitialized(false);
      dispatch.stripeTerminal.setShouldAutoReconnect(false);

      // In case the reader is connecting, we will want to wait for that connection attempt
      // to complete before disconnecting. The function throws if the reader is disconnected or if
      // the connection timed out, either of which we don't care about since we're about to force
      // disconnect the reader.
      try {
        await waitForReaderConnection({ getState });
      } catch (e) {
        // Don't do anything, incl. logging. See comment above.
      }

      try {
        await dispatch.stripeTerminal.disconnectReader({ force: true });
      } catch (e) {
        nonCriticalException(e);
      }
    }, [
      isInitialized,
      dispatch.stripeTerminal,
      getState,
      setIsInitialized,
    ],
  );

  React.useEffect(() => {
    if (!installReconnectHandler || !tapToPayEnabled || !shouldAutoReconnect) return () => {};

    // onAppResume returns a cleanup function
    return onAppResume(async () => {
      log('Reconnecting…');
      await connectReader();
    });
  }, [
    connectReader,
    installReconnectHandler,
    shouldAutoReconnect,
    tapToPayEnabled,
  ]);

  return {
    connectReader,
    initReaderConnection,
    killReaderConnection,
  };
}
